// The screen for a TV or a second monitor: the current reading in big type, the last 6 hours,
// and today's numbers. It refreshes on the phone's schedule, keeps the screen awake, hides the
// pointer when idle, and reloads itself after a deploy. Styled by now.css only (see there why).

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type { BandsDTO, LatestDTO, MeDTO, PumpDTO, WindowDTO } from "../../shared/dto.js";
import { TARGET_HIGH, TARGET_LOW } from "../../shared/glucose.js";
import { entryEmoji, headline } from "../../shared/labels.js";
import { nextDelayMs } from "../../shared/refresh.js";
import { api } from "@/api";
import { GlucoseChart } from "@/charts/GlucoseChart";
import { BANDS } from "@/charts/RangeBar";
import { ago, arrow, carbs, delta, entryClock, formatTime, insulin, longDate, mgdl, percent, todayIn } from "@/lib/format";
import { applyTheme, storedTheme } from "@/lib/theme";
import { useElementSize } from "@/lib/useElementSize";
import { useNow } from "@/lib/useNow";
import "../now.css";

const SPAN_MS = 6 * 3_600_000;
const QUARTER_MS = 15 * 60_000;
const STALE_MS = 15 * 60_000;
const IDLE_MS = 4_000;

export function NowPage({ me, updateReady }: { me: MeDTO; updateReady: boolean }) {
  const now = useNow(10_000);
  // The window moves in 15-minute steps, with 5 to 20 minutes of room after now, so the query
  // key changes four times an hour instead of on every tick.
  const end = Math.ceil((now + 5 * 60_000) / QUARTER_MS) * QUARTER_MS;
  const start = end - SPAN_MS;
  const today = todayIn(me.zone, now);

  const recent = useQuery({
    queryKey: ["window", "range", start, end],
    queryFn: () => api.window({ from: start, to: end }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => nextDelayMs(q.state.data?.latest?.t ?? null, Date.now()),
  });
  const day = useQuery({
    queryKey: ["window", today],
    queryFn: () => api.window({ date: today }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => nextDelayMs(q.state.data?.latest?.t ?? null, Date.now()),
  });

  useDarkByDefault();
  useWakeLock();
  const idle = useIdle(IDLE_MS);
  const [fullscreen, toggleFullscreen] = useFullscreen();
  const [chartRef, chartSize] = useElementSize<HTMLDivElement>();

  useEffect(() => {
    if (updateReady) window.location.reload();
  }, [updateReady]);

  const data = recent.data;
  const latest = data?.latest ?? null;
  const stale = !latest || now - latest.t > STALE_MS;
  const offline = recent.isError || day.isError;
  // Read from across a room: labels grow with the screen.
  const scale = Math.max(1, Math.min(2.4, chartSize.height / 250));

  return (
    <div className={`now${idle ? " now-idle" : ""}${stale ? " now-stale" : ""}`}>
      <div className="now-head">
        <Reading latest={latest} now={now} loading={recent.isPending} />
        <div className="now-clock">
          <div className="now-time">{formatTime(now, me.zone)}</div>
          <div className="now-date">{longDate(today, { weekday: "long", month: "long", day: "numeric" })}</div>
          {offline && <div className="now-offline">Can't reach Nightscout{data ? ` · data from ${formatTime(data.fetchedAt, me.zone)}` : ""}</div>}
        </div>
      </div>

      <div className="now-chart" ref={chartRef}>
        {data && chartSize.height > 0 ? (
          <GlucoseChart data={data} height={chartSize.height} nowMs={now} scale={scale} interactive={false} />
        ) : (
          <div className="now-empty">{recent.isPending ? "Loading…" : "No data"}</div>
        )}
      </div>

      <Footer day={day.data} recent={data} now={now} zone={me.zone} />

      <div className="now-controls">
        <Link to="/day" className="now-button">
          Day view
        </Link>
        {fullscreenSupported() && (
          <button type="button" className="now-button" onClick={toggleFullscreen}>
            {fullscreen ? "Exit full screen" : "Full screen"}
          </button>
        )}
      </div>
    </div>
  );
}

function Reading({ latest, now, loading }: { latest: LatestDTO | null; now: number; loading: boolean }) {
  if (!latest) {
    return (
      <div>
        <div className="now-value">–</div>
        <div className="now-detail">{loading ? "Loading…" : <span className="now-warn">No readings in the last 6 hours</span>}</div>
      </div>
    );
  }
  const age = now - latest.t;
  return (
    <div>
      <div className="now-reading">
        <span className="now-value" style={{ color: bandColor(latest.mgdl) }}>
          {latest.mgdl}
        </span>
        <span className="now-arrow" style={{ color: bandColor(latest.mgdl) }}>
          {arrow(latest.direction)}
        </span>
      </div>
      <div className="now-detail">
        {age > STALE_MS ? (
          <span className="now-warn">No reading for {ago(latest.t, now).replace(" ago", "")}</span>
        ) : (
          <>
            {latest.delta !== null && <>{delta(latest.delta)} mg/dL · </>}
            {ago(latest.t, now)}
          </>
        )}
      </div>
    </div>
  );
}

function Footer({ day, recent, now, zone }: { day: WindowDTO | undefined; recent: WindowDTO | undefined; now: number; zone: string }) {
  const m = day?.summary.metrics;
  const lastBolus = useMemo(() => lastWith(recent?.pump ?? [], (p) => p.insulin), [recent]);
  const lastCarbs = useMemo(() => lastWith(recent?.pump ?? [], (p) => p.carbs), [recent]);
  const entries = useMemo(() => (day?.entries ?? []).slice(-3).reverse(), [day]);

  return (
    <div>
      <div className="now-foot">
        <div className="now-stat">
          <div className="now-label">In range today</div>
          <div className="now-big" style={{ color: "var(--band-in-range)" }}>
            {percent(m?.bands?.inRange ?? null)}
          </div>
          <Bar bands={m?.bands ?? null} />
        </div>
        <div className="now-stat">
          <div className="now-label">Mean today</div>
          <div className="now-big">
            {mgdl(m?.mean ?? null)} <span className="now-small">mg/dL</span>
          </div>
        </div>
        <div className="now-stat">
          <div className="now-label">Carbs · bolus today</div>
          <div className="now-big">
            {day ? `${carbs(day.summary.carbs)} · ${insulin(day.summary.bolusInsulin)}` : "–"}
          </div>
        </div>
        <div className="now-stat">
          <div className="now-label">Last bolus</div>
          <div className="now-big">{lastBolus ? insulin(lastBolus.insulin!) : "–"}</div>
          <div className="now-small">{lastBolus ? `${formatTime(lastBolus.t, zone)} · ${ago(lastBolus.t, now)}` : "none in 6 h"}</div>
        </div>
        <div className="now-stat">
          <div className="now-label">Last carbs</div>
          <div className="now-big">{lastCarbs ? carbs(lastCarbs.carbs!) : "–"}</div>
          <div className="now-small">{lastCarbs ? `${formatTime(lastCarbs.t, zone)} · ${ago(lastCarbs.t, now)}` : "none in 6 h"}</div>
        </div>
      </div>
      {entries.length > 0 && (
        <div className="now-entries">
          {entries.map((e) => (
            <span key={e.id} className="now-entry">
              {entryEmoji(e)} {headline(e)} <span className="now-entry-time">· {entryClock(e, zone)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Bar({ bands }: { bands: BandsDTO | null }) {
  return (
    <div className="now-bar">
      {bands && BANDS.map((b) => (bands[b.key] > 0 ? <div key={b.key} style={{ width: `${bands[b.key]}%`, background: b.color }} /> : null))}
    </div>
  );
}

function lastWith(pump: PumpDTO[], value: (p: PumpDTO) => number | null): PumpDTO | null {
  for (let i = pump.length - 1; i >= 0; i--) {
    const p = pump[i]!;
    if ((value(p) ?? 0) > 0) return p;
  }
  return null;
}

/** The five AGP colours, so a low reads red and a high amber from across the room. */
function bandColor(value: number): string {
  if (value < 54) return "var(--band-very-low)";
  if (value < TARGET_LOW) return "var(--band-low)";
  if (value <= TARGET_HIGH) return "var(--band-in-range)";
  if (value <= 250) return "var(--band-high)";
  return "var(--band-very-high)";
}

// ---- Screen helpers ---------------------------------------------------------------------

/** A TV is usually in a dim room: dark unless a theme was picked. */
function useDarkByDefault() {
  useEffect(() => {
    applyTheme(storedTheme(), "dark");
    return () => applyTheme(storedTheme());
  }, []);
}

/** Keeps the screen on while the page is visible. The lock drops when the tab is hidden, so it's taken again. */
function useWakeLock() {
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let active = true;
    async function acquire() {
      if (document.visibilityState !== "visible" || (lock && !lock.released)) return;
      try {
        const next = await navigator.wakeLock.request("screen");
        if (active) lock = next;
        else void next.release();
      } catch {
        // Battery saver or no permission: the screen may sleep, nothing else to do.
      }
    }
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", acquire);
      void lock?.release();
    };
  }, []);
}

/** True after `ms` without pointer or key activity. */
function useIdle(ms: number) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let timer = window.setTimeout(() => setIdle(true), ms);
    function wake() {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), ms);
    }
    const events = ["pointermove", "pointerdown", "keydown"] as const;
    for (const e of events) window.addEventListener(e, wake);
    return () => {
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, wake);
    };
  }, [ms]);
  return idle;
}

function fullscreenSupported() {
  return typeof document.documentElement.requestFullscreen === "function";
}

function useFullscreen() {
  const [on, setOn] = useState(() => !!document.fullscreenElement);
  useEffect(() => {
    const update = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  function toggle() {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => {});
  }
  return [on, toggle] as const;
}
